/**
 * Formatage des nombres et des durées — logique pure (§4 : aucune dépendance externe).
 *
 * Pourquoi un formateur maison plutôt que `Intl.NumberFormat({ notation: 'compact' })` :
 *  1. Intl s'arrête aux « trillions » (1e12) alors qu'un idle dépasse allègrement 1e30 ;
 *  2. Intl rend « 1.2K » en anglais, alors que la convention des idle games — et les
 *     exemples de la spec §6 — veulent « 1.2k » ;
 *  3. le résultat doit être 100 % déterministe et testable sans dépendre de la base ICU
 *     embarquée dans le navigateur.
 *
 * Choix d'échelle : `k`, `M`, `T`, puis `aa`, `ab`, `ac`… sont communs aux deux langues.
 * SEUL le milliard diffère : `Md` en français, `B` en anglais (§6). Un joueur ne voit jamais
 * que sa propre langue à l'écran — interface et classements compris — donc les deux notations
 * ne cohabitent jamais, et « 1,2 B » ne veut rien dire pour un francophone.
 * Sont également localisés le séparateur décimal, le séparateur de milliers et l'espace avant
 * le suffixe : « 1,2 k » / « 3,4 M » en FR, « 1.2k » / « 3.4M » en EN.
 */

export type LocaleCode = 'fr' | 'en';

interface NumberSymbols {
  /** Séparateur décimal. */
  readonly decimal: string;
  /** Séparateur de groupes de milliers (nombres non abrégés uniquement). */
  readonly group: string;
  /** Espace inséré entre la mantisse et le suffixe. */
  readonly suffixSpace: string;
  /**
   * Suffixes nommés, du rang 0 (unité) au rang 4 (1e12). Toutes les langues doivent en
   * déclarer le même nombre : le rang maximal représentable en dépend.
   */
  readonly namedSuffixes: readonly [string, string, string, string, string];
}

/**
 * U+202F : espace fine insécable, typographie française correcte.
 * Toujours écrite en séquence d'échappement, jamais collée telle quelle : le caractère est
 * invisible et se ferait remplacer silencieusement par une espace ordinaire au moindre
 * copier-coller — un bug de rendu que la relecture ne peut pas repérer.
 */
const NARROW_NBSP = '\u202f';

const SYMBOLS: Readonly<Record<LocaleCode, NumberSymbols>> = {
  // « Md » (milliard) est le standard français ; « B » n'y évoque rien.
  fr: {
    decimal: ',',
    group: NARROW_NBSP,
    suffixSpace: NARROW_NBSP,
    namedSuffixes: ['', 'k', 'M', 'Md', 'T'],
  },
  en: {
    decimal: '.',
    group: ',',
    suffixSpace: '',
    namedSuffixes: ['', 'k', 'M', 'B', 'T'],
  },
};

/** Nombre de suffixes nommés, identique dans toutes les langues (rangs 0 à 4, jusqu'à 1e12). */
const NAMED_SUFFIX_COUNT = SYMBOLS.en.namedSuffixes.length;

const ALPHABET_SIZE = 26;
const LETTER_PAIR_COUNT = ALPHABET_SIZE * ALPHABET_SIZE;
/** Dernier rang représentable : 1e(3 × MAX_RANK) ≈ 1e2043. Très au-delà de tout endgame. */
const MAX_RANK = NAMED_SUFFIX_COUNT + LETTER_PAIR_COUNT - 1;

const LOWERCASE_A = 97;

function symbolsFor(locale: LocaleCode): NumberSymbols {
  return SYMBOLS[locale];
}

/**
 * Suffixe d'un rang (rang 0 = unité, rang 1 = milliers, rang 2 = millions…).
 * Le rang 3 est le seul qui dépend de la langue (`Md` en FR, `B` en EN) ; au-delà de `T`,
 * la séquence `aa`, `ab`, … `zz` est commune.
 */
export function suffixForRank(rank: number, locale: LocaleCode): string {
  const { namedSuffixes } = symbolsFor(locale);

  if (rank < namedSuffixes.length) {
    return namedSuffixes[rank] ?? '';
  }
  const index = rank - namedSuffixes.length;
  const first = Math.floor(index / ALPHABET_SIZE);
  const second = index % ALPHABET_SIZE;
  return String.fromCharCode(LOWERCASE_A + first) + String.fromCharCode(LOWERCASE_A + second);
}

/** Arrondi à N décimales sans les artefacts flottants de `toFixed` sur les grands nombres. */
function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Applique le séparateur décimal local à une écriture décimale « brute » (point). */
function localizeDecimalPoint(raw: string, symbols: NumberSymbols): string {
  return raw.replace('.', symbols.decimal);
}

/** Insère le séparateur de milliers dans une partie entière (chaîne de chiffres). */
function groupThousands(digits: string, symbols: NumberSymbols): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, symbols.group);
}

export interface FormatNumberOptions {
  /**
   * Nombre de décimales sous 1000. Par défaut : 0 si la valeur est entière, sinon 1.
   * Utile pour la production par seconde (« 0,5 /s »).
   */
  readonly smallDecimals?: number;
}

/**
 * Formate une quantité de jeu (pièces, production, gemmes…) en notation compacte localisée.
 *
 * Règles :
 *  - |v| < 1000  → écriture complète, groupée (« 1 234 » ne se produit pas ici mais la
 *                  fonction reste correcte si le seuil évolue) ;
 *  - au-delà     → mantisse + suffixe, 1 décimale tant que la mantisse < 100, 0 ensuite.
 *
 * La décimale n'est jamais « rognée » (on affiche « 1,0 k » et non « 1 k ») : dans un HUD
 * qui se met à jour 10 fois par seconde, une largeur de texte stable évite le scintillement.
 */
export function formatNumber(
  value: number,
  locale: LocaleCode,
  options: FormatNumberOptions = {},
): string {
  const symbols = symbolsFor(locale);

  if (!Number.isFinite(value)) {
    return value > 0 ? '∞' : '0';
  }

  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);

  if (abs < 1000) {
    const decimals = options.smallDecimals ?? (Number.isInteger(abs) ? 0 : 1);
    const rounded = roundTo(abs, decimals);
    // Cas limite : 999.96 arrondi à 1 décimale donne 1000.0 → on repasse par la branche compacte.
    if (rounded < 1000) {
      const [integerPart = '0', fractionPart] = rounded.toFixed(decimals).split('.');
      const grouped = groupThousands(integerPart, symbols);
      return (
        sign + (fractionPart === undefined ? grouped : grouped + symbols.decimal + fractionPart)
      );
    }
  }

  let rank = 0;
  let mantissa = abs;
  while (mantissa >= 1000 && rank < MAX_RANK) {
    mantissa /= 1000;
    rank += 1;
  }

  let decimals = mantissa < 100 ? 1 : 0;
  let rounded = roundTo(mantissa, decimals);

  // L'arrondi peut faire franchir le palier (999,97 k → 1000,0 k). On remonte d'un rang.
  if (rounded >= 1000 && rank < MAX_RANK) {
    rank += 1;
    mantissa = rounded / 1000;
    decimals = mantissa < 100 ? 1 : 0;
    rounded = roundTo(mantissa, decimals);
  }

  const body = localizeDecimalPoint(rounded.toFixed(decimals), symbols);
  const suffix = suffixForRank(rank, locale);

  return sign + body + (suffix === '' ? '' : symbols.suffixSpace + suffix);
}

/**
 * Formate un entier en toutes lettres avec séparateurs de milliers localisés.
 * Réservé aux petits compteurs exacts : visites, niveaux, rang de ligue.
 */
export function formatInteger(value: number, locale: LocaleCode): string {
  const symbols = symbolsFor(locale);

  if (!Number.isFinite(value)) {
    return '0';
  }

  const sign = value < 0 ? '-' : '';
  const digits = Math.floor(Math.abs(value)).toString();
  return sign + groupThousands(digits, symbols);
}

/** Formate un pourcentage de multiplicateur (« +100 % » / « +100% »). */
export function formatPercent(ratio: number, locale: LocaleCode): string {
  const symbols = symbolsFor(locale);
  const percent = roundTo(ratio * 100, Number.isInteger(ratio * 100) ? 0 : 1);
  const body = localizeDecimalPoint(percent.toString(), symbols);
  // En français, l'espace insécable avant « % » est obligatoire.
  return body + (locale === 'fr' ? `${NARROW_NBSP}%` : '%');
}

export interface DurationParts {
  readonly days: number;
  readonly hours: number;
  readonly minutes: number;
  readonly seconds: number;
  readonly totalSeconds: number;
}

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86_400;

/**
 * Décompose une durée en jours/heures/minutes/secondes.
 * Les libellés (« 2 h 04 ») sont composés côté i18n : `src/core` ne connaît aucun texte.
 */
export function splitDuration(milliseconds: number): DurationParts {
  const safe = Number.isFinite(milliseconds) && milliseconds > 0 ? milliseconds : 0;
  const totalSeconds = Math.floor(safe / MS_PER_SECOND);

  return {
    days: Math.floor(totalSeconds / SECONDS_PER_DAY),
    hours: Math.floor((totalSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR),
    minutes: Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE),
    seconds: totalSeconds % SECONDS_PER_MINUTE,
    totalSeconds,
  };
}

/**
 * Rendu « horloge » d'une durée, neutre linguistiquement : `H:MM:SS` ou `MM:SS`.
 * Utilisé pour les minuteurs de boost, identiques dans toutes les langues.
 */
export function formatClock(milliseconds: number): string {
  const { days, hours, minutes, seconds } = splitDuration(milliseconds);
  const totalHours = days * 24 + hours;
  const paddedMinutes = minutes.toString().padStart(2, '0');
  const paddedSeconds = seconds.toString().padStart(2, '0');

  return totalHours > 0
    ? `${totalHours}:${paddedMinutes}:${paddedSeconds}`
    : `${paddedMinutes}:${paddedSeconds}`;
}
